import React from 'react';
import { Box, IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Add, DeleteOutline } from '@mui/icons-material';

import { TimeRangeInput } from '../../../Inputs/TimeRangeInput';
import { ArrayHelpers, FieldArray, useFormikContext } from 'formik';
import { Button } from '../../../Button';
import { AvailabilityFormValues } from './AvailabilityForm.types';
import { useCalendarEventPopoverActions } from '../../../Calendar';
import { CalendarDateService } from '@uplift/services';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';


export type AvailabilityFormProps = {
  warningOnLessThenHour?: boolean;
  isMobile?: boolean;
};

const useStyles = makeStyles()(theme => ({
  item: {
    marginBottom: theme.spacing(3),

    '&:last-child': {
      marginBottom: 0,
    }
  },
  itemMobile: {
    marginBottom: theme.spacing(1),

    '&:last-child': {
      marginBottom: 0,
    }
  }
}));

export const AvailabilityForm: React.FC<AvailabilityFormProps> = ({ warningOnLessThenHour = false, isMobile = false }) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const { classes } = useStyles();
  const { values } = useFormikContext<AvailabilityFormValues>();

  const calendarEventPopoverActions = useCalendarEventPopoverActions();

  React.useEffect(() => {
    calendarEventPopoverActions.current.updatePosition();
  }, [ values.availability ]); // eslint-disable-line react-hooks/exhaustive-deps

  const isTwoButtonsAppend = !isMobile;
  const isInlineAddButtonShown = !isMobile;
  const isOnlyFirstLabelsShown = isMobile;

  return (
    <Box>
      <FieldArray
        name="availability"
        render={(arrayHelpers: ArrayHelpers) => {
          if (values.availability.length) {
            const lastAvailability = values.availability[values.availability.length - 1];
            const lastAvailabilityTime = CalendarDateService.date.tz(lastAvailability.end, 'HH:mm:ss', timezone);

            return (
              <Box>
                {values.availability.map((a, index, arr) => (
                  <Box key={a.id} className={isMobile ? classes.itemMobile : classes.item}>
                    <TimeRangeInput
                      fullWidth
                      lessThenHourWarningTitle={l10n.calendar.availability.editForm.lessThenHourWarningTitle}
                      warningOnLessThenHour={warningOnLessThenHour}
                      startTitle={isOnlyFirstLabelsShown && index > 0 ? '' : l10n.calendar.availability.editForm.startTime}
                      endTitle={isOnlyFirstLabelsShown && index > 0 ? '' : l10n.calendar.availability.editForm.endTime}
                      name={`availability.${index}`}
                      append={(
                        <Box width={isTwoButtonsAppend ? '96px' : '48px'}>
                          <IconButton onClick={() => arrayHelpers.remove(index)}><DeleteOutline /></IconButton>
                          {isInlineAddButtonShown && index + 1 >= arr.length && (
                            <IconButton
                              onClick={() => {
                                arrayHelpers.push({
                                  id: Date.now(),
                                  start: lastAvailabilityTime.add(1, 'hour').format('HH:mm:ss'),
                                  end: null,
                                });
                              }}
                            >
                              <Add />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    />
                  </Box>
                ))}
                {isMobile && (
                  <Box mt={2}>
                    <Button
                      color="secondary"
                      startIcon={<Add />}
                      fullWidth
                      onClick={() => {
                        arrayHelpers.push({
                          id: Date.now(),
                          start: lastAvailabilityTime.add(1, 'hour').format('HH:mm:ss'),
                          end: null,
                        });
                      }}
                    >
                      {l10n.calendar.availability.editForm.addTimeBlock}
                    </Button>
                  </Box>
                )}
              </Box>
            );
          }

          return (
            <Button
              fullWidth
              startIcon={<Add />}
              variant="outlined"
              size="large"
              onClick={() => {
                arrayHelpers.push({ id: Date.now(), start: '09:00:00', end: '17:00:00' });
              }}
            >
              {l10n.calendar.availability.editForm.addTimeBlock}
            </Button>
          );
        }}
      />
    </Box>
  );
};
